/* Vista Contact — "Plan the route" form. Travel-booking ledger feel
   with coordinates, departure dates, magazine stamps. */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";

export default function VistaContact() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  useEffect(() => { document.title = `${studioName} — Plan the Route`; }, [studioName]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Add your name, email, and a short brief."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", email: "", phone: "", message: "" });
      toast.success("Route received. We will confirm departure within 48 hours.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not transmit — try again."); }
    finally { setSending(false); }
  }

  return (
    <div data-testid="vista-contact" style={{ background: "var(--tenant-bg, #F8F9FB)", color: "var(--tenant-fg, #102A33)" }}>
      <section className="px-6 lg:px-12 pt-16 pb-12 border-b" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
        <div className="max-w-[1480px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
          <div className="md:col-span-3">
            <div className="text-[9px] uppercase tracking-[0.42em] mb-3" style={{ color: "var(--tenant-accent)" }}>Itinerary Desk · {new Date().getFullYear()}</div>
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>40°43′N · 74°00′W</div>
          </div>
          <div className="md:col-span-9">
            <h1 className="leading-[0.95]" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              Plan the route.
            </h1>
          </div>
        </div>
      </section>

      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        {/* Left ledger */}
        <aside className="md:col-span-4 space-y-5 text-sm">
          <div className="text-[9px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>Operating from</div>
          <div style={{ color: "var(--tenant-fg-soft)" }}>{settings?.contact_location || "By appointment"}</div>
          <div className="pt-5 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
            <div className="text-[9px] uppercase tracking-[0.42em] mb-1" style={{ color: "var(--tenant-accent)" }}>Direct</div>
            {settings?.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="block hover:underline" style={{ color: "var(--tenant-fg-soft)" }}>{settings.contact_email}</a>
            )}
            {settings?.contact_phone && (
              <a href={`tel:${settings.contact_phone}`} className="block hover:underline" style={{ color: "var(--tenant-fg-soft)" }}>{settings.contact_phone}</a>
            )}
          </div>
          <div className="pt-5 border-t" style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }}>
            <div className="text-[9px] uppercase tracking-[0.42em] mb-1" style={{ color: "var(--tenant-accent)" }}>Response</div>
            <div style={{ color: "var(--tenant-fg-soft)" }}>Within 48 hours, year-round.</div>
          </div>
        </aside>

        {/* Right form */}
        <form onSubmit={onSubmit} className="md:col-span-8 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { name: "name", label: "Your name", placeholder: "Captain’s name", type: "text" },
              { name: "email", label: "Email", placeholder: "you@signal.com", type: "email" },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-[9px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>{f.label}</label>
                <input type={f.type} placeholder={f.placeholder} value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`vista-contact-${f.name}`}
                  className="w-full bg-transparent border-2 px-3 py-2.5 outline-none focus:border-current"
                  style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }} />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Phone (optional)</label>
            <input type="tel" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="vista-contact-phone"
              className="w-full bg-transparent border-2 px-3 py-2.5 outline-none focus:border-current"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }} />
          </div>
          <div>
            <label className="block text-[9px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Brief — where, when, why</label>
            <textarea rows={6} value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="vista-contact-message"
              className="w-full bg-transparent border-2 px-3 py-2.5 outline-none focus:border-current resize-none"
              style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 30%, transparent)" }} />
          </div>
          <button type="submit" disabled={sending} data-testid="vista-contact-submit"
            className="inline-flex items-center gap-3 px-7 py-3 text-[10px] uppercase tracking-[0.42em] border-2 disabled:opacity-60"
            style={{ borderColor: "var(--tenant-accent)", color: "var(--tenant-accent)" }}>
            {sending ? "Transmitting..." : "Lodge the itinerary →"}
          </button>
        </form>
      </div>
    </div>
  );
}
